import React, { forwardRef, useState } from 'react'

export interface TextAreaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'style'> {
    variant?: 'primary' | 'secondary'
    label?: string
    description?: string
    errorMessage?: string
    isInvalid?: boolean
    showLabel?: boolean
    showDescription?: boolean
    maxCharacters?: number
    componentStyle?: React.CSSProperties
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
    (
        {
            variant = 'primary',
            label,
            description,
            errorMessage,
            isInvalid = false,
            showLabel = true,
            showDescription = true,
            maxCharacters,
            componentStyle,
            ...props
        },
        ref
    ) => {
        const isSecondary = variant === 'secondary'
        const [charCount, setCharCount] = useState((props.defaultValue as string)?.length ?? 0)

        const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            setCharCount(e.target.value.length)
            props.onChange?.(e)
        }

        const counterText = maxCharacters !== undefined ? `Characters: ${charCount}/${maxCharacters}` : description

        const isOverLimit = maxCharacters !== undefined && charCount > maxCharacters
        const hasError = isInvalid || isOverLimit

        return (
            <div className={`flex min-w-[116px] flex-col gap-1 ${props.disabled ? 'opacity-50' : ''}`} style={componentStyle}>
                {showLabel && label && (
                    <label
                        className={`inline-flex flex-wrap items-center gap-1 whitespace-nowrap pr-2 text-sm font-medium leading-[1.43] ${hasError ? 'text-danger' : 'text-foreground'}`}
                    >
                        {label}
                    </label>
                )}

                <div
                    className={`relative min-h-9 flex-1 overflow-hidden rounded-xl transition-[background,box-shadow] duration-150
                        ${isSecondary ? 'bg-default' : 'bg-surface'}
                        ${!isSecondary && !props.disabled ? 'not-focus-within:hover:bg-surface-secondary' : ''}
                        ${hasError ? 'ring-2 ring-danger' : `focus-within:ring-2 focus-within:ring-accent ${isSecondary ? '' : 'shadow-field'}`}`}
                >
                    <textarea
                        ref={ref}
                        {...props}
                        onChange={handleChange}
                        className="box-border h-full min-h-20 w-full resize-y border-none bg-transparent px-3 py-2 text-sm font-normal leading-5 text-foreground outline-none placeholder:text-foreground-muted"
                    />
                </div>

                {showDescription &&
                    (hasError && (errorMessage || isOverLimit) ? (
                        <span className="text-sm font-normal leading-[1.43] text-danger">{isOverLimit ? counterText : errorMessage}</span>
                    ) : counterText ? (
                        <span className="text-sm font-normal leading-[1.43] text-foreground-muted">{counterText}</span>
                    ) : null)}
            </div>
        )
    }
)

TextArea.displayName = 'TextArea'
